import * as React from 'react';
// Import material ui modules
import {
	Check,
	Close,
} from '@mui/icons-material';
// Import hooks.
import {
	useInputAddressLine1,
	UseInputAddressLine1Results,
	useInputAutoComplete,
	UseInputAutoCompleteResults,
	UseInputFormResult,
	useInputPostalCode,
	UseInputPostalCodeResults,
	useInputText,
	UseInputTextResults,
} from '@app/hooks/view/input';
// Import props.
import {
	FormPopupProps,
	NavigationFooterProps,
	ScrollbarProps,
	FormPopupSingleLineAddressVerificationProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
	PopupWidth,
} from '@app/enumerations';
// Import api models.
import {
    Address,
	PossibleState,
} from '@app/models/api';
// Import app models.
import {
	AutoCompleteItem
} from '@app/models/app';

interface UseFormPopupSingleLineAddressVerificationParameters {
	id: string;
	props: FormPopupSingleLineAddressVerificationProps,
	showValidations?: boolean;
}

interface UseFormPopupSingleLineAddressVerificationResults {
	submitForm: () => void;
	address1Input: UseInputAddressLine1Results;
	address2Input: UseInputTextResults;
	cityInput: UseInputTextResults;
	stateInput: UseInputAutoCompleteResults;
	postalCodeInput: UseInputPostalCodeResults;
	applyFormProps: () => FormPopupProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
}

const useFormPopupSingleLineAddressVerification = (params: UseFormPopupSingleLineAddressVerificationParameters): UseFormPopupSingleLineAddressVerificationResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (): void => {
		// First flag all inputs to show any validation errors
		flagAllInputsToShowValidations();
		// Validate the whole form.
		let isValid: boolean = checkFormValidationWithFocus();
		// Make sure the confirmation popup appears if it's required.
		if (isValid) {
			params.props.onSaveAddress({
				addressLine1: address1Input.value,
				addressLine2: address2Input.value,
				city: cityInput.value,
				state: stateInput.value ?? String.empty(),
				postalCode: postalCodeInput.value,
			});		
		}
	}

	/**
 * Returns props for form. 
 **/
	const applyFormProps = (): FormPopupProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Address Verification',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: true,
			isOpen: params.props.isOpen,
			popupWidth: PopupWidth.MEDIUM,
			onCloseClick: params.props.onClose,
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Close',
			leftButtonClick: params.props.onClose,
			leftButtonIcon: Close,
			rightButtonDisabled: false,
			rightButtonLabel: 'Save',
			rightButtonClick: submitForm,
			rightButtonIcon: Check,
			alignment: NavigationFooterAlignment.RIGHT,
		};
	};

	// #endregion exported methods


	// #region private methods

	const onSelectSuggestedAddress = (address: Address): void => {
		address2Input.setInput(address.addressLine2 ?? String.empty());
		cityInput.setInput(address.city ?? String.empty());
		stateInput.setInput(address.state ?? String.empty());
		postalCodeInput.setInput(address.postalCode ?? String.empty());
	}

	/**
	 * Validates an individual input with focus.
	 * @param input
	 */
	const checkInputValidationWithFocus = <T>(input: UseInputFormResult<T>): boolean => {
		let isValid = input.isInputValid;
		if (!isValid) {
			input.focusToInput();
		}
		return isValid;
	}

	/**
	 * Method to check form validation with focus. 
	 **/
	const checkFormValidationWithFocus = (): boolean => {
		return checkInputValidationWithFocus<string>(address1Input)
			&& checkInputValidationWithFocus<string>(address2Input)
			&& checkInputValidationWithFocus<string>(cityInput)
			&& checkInputValidationWithFocus<string | undefined>(stateInput)
			&& checkInputValidationWithFocus<string>(postalCodeInput);
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		address1Input.changeShowValidationFlag(true);
		address2Input.changeShowValidationFlag(true);
		cityInput.changeShowValidationFlag(true);
		stateInput.changeShowValidationFlag(true);
		postalCodeInput.changeShowValidationFlag(true);
	}

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Generates id for address line 1. 
	 **/
	const generateIdForAddressLine1 = (): string => {
		return `${params.id}-addressLine1`;
	}

	/**
	 * Generates id for address line 2. 
	 **/
	const generateIdForAddressLine2 = (): string => {
		return `${params.id}-addressLine2`;
	}

	/**
	 * Generates id for city. 
	 **/
	const generateIdForCity = (): string => {
		return `${params.id}-city`;
	}

	/**
	 * Generates id for state.
	 **/
	const generateIdForState = (): string => {
		return `${params.id}-state`;
	}

	/**
	 * Generates id for postal code.
	 **/
	const generateIdForPostalCode = (): string => {
		return `${params.id}-postalCode`;
	}

	const convertStatesToAutoCompleteItems = (ignoreRiskAllowed: Boolean): AutoCompleteItem[] => {
		return params.props.possibleStates
		.filter((possibleState: PossibleState) => ignoreRiskAllowed || (possibleState.isRiskAllowed !== null && possibleState.isRiskAllowed))
		.map((possibleState: PossibleState) => {
			return new AutoCompleteItem({
				label: possibleState.name ?? String.empty(),
				value: possibleState.code ?? String.empty(),
				comparatorValue: possibleState.name ?? undefined,
			})
		})
	} 

	// #endregion private methods


	// #region variables

	// Setup the address 1 input hook.
	const address1Input: UseInputAddressLine1Results = useInputAddressLine1({
		id: generateIdForAddressLine1(),
		required: true,
		disabled: false,
		value: params.props.verifiedAddress.addressLine1 ?? String.empty(),
		showValidation: params.showValidations,
		onGetAddressSuggestions: params.props.onGetAddressSuggestions,
		onSelectSuggestedAddress: onSelectSuggestedAddress, 
	});

	// Setup the address 2 input hook.
	const address2Input: UseInputTextResults = useInputText({
		id: generateIdForAddressLine2(),
		label: 'Address 2',
		required: false,
		disabled: false,
		value: params.props.verifiedAddress.addressLine2 ?? String.empty(),
		showValidation: params.showValidations,
	});

	// Setup the city input hook.
	const cityInput: UseInputTextResults = useInputText({
		id: generateIdForCity(),
		label: 'City',
		required: true,
		disabled: false,
		value: params.props.verifiedAddress.city ?? String.empty(),
		showValidation: params.showValidations,
	});

	// Setup the state input hook.
	const stateInput: UseInputAutoCompleteResults = useInputAutoComplete({
		id: generateIdForState(),
		label: 'State',
		required: true,
		disabled: false,
		value: params.props.verifiedAddress.state ?? String.empty(),
		autoCompleteItems: convertStatesToAutoCompleteItems(params.props.ignorePossibleStateRiskAllowed),
		maxResults: params.props.possibleStates?.length,
		showValidation: params.showValidations,
	});

	// Setup the postal code input hook.
	const postalCodeInput: UseInputPostalCodeResults = useInputPostalCode({
		id: generateIdForPostalCode(),
		label: 'Zip',
		required: true,
		disabled: false,
		value: params.props.verifiedAddress.postalCode ?? String.empty(),
		showValidation: params.showValidations,
	});

	// #endregion private variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// If validations are to be shown, then initially scroll to the first invalidated input.
		if (!params.showValidations
			|| checkFormValidationWithFocus()) {
			// If validations pass or are not to be shown, then initially the first input should be focused.
			address1Input.focusToInput();
		}
	}, []);

	// #endregion life-cycle methods.


	return {
		submitForm: submitForm,
		address1Input: address1Input,
		address2Input: address2Input,
		cityInput: cityInput,
		stateInput: stateInput,
		postalCodeInput: postalCodeInput,
		applyFormProps: applyFormProps,
		applyNavigationFooterProps: applyNavigationFooterProps,
	};
}

// Export parameters.
export {
	UseFormPopupSingleLineAddressVerificationParameters,
}

// Export results.
export {
	UseFormPopupSingleLineAddressVerificationResults,
}

export default useFormPopupSingleLineAddressVerification;